// API functions for the import tool.
// Supports YAML/JSON file upload, validation, and execution.

import type { ApiClient } from './client'

// --- Types ---

/** Validation error returned by the backend for import payloads. */
export interface ImportValidationError {
  section: string
  token: string
  message: string
}

/** Response from POST /import and PUT /import/{id}. */
export interface ImportUploadResponse {
  id: string
  status: string
  validation_errors: ImportValidationError[]
}

/** Response from POST /import/{id}/validate. */
export interface ImportValidateResponse {
  validation_errors: ImportValidationError[]
}

/** Full pending import record from GET /import/{id}. */
export interface PendingImport {
  id: string
  tenant_id: string
  user_id: string
  status: 'pending_review' | 'executing' | 'completed' | 'failed'
  payload: Record<string, unknown>
  validation_errors: ImportValidationError[]
  execution_report?: ExecutionReport | null
  created_at: string
  updated_at: string
}

/** Execution report returned after successful import execution. */
export interface ExecutionReport {
  started_at: string
  finished_at: string
  duration_ms: number
  phases: ExecutionPhase[]
  summary: ExecutionSummary
}

export interface ExecutionPhase {
  name: string
  results: ExecutionResult[]
}

export interface ExecutionResult {
  token: string
  status: 'created' | 'updated' | 'skipped' | 'failed'
  message: string
  id?: string
}

export interface ExecutionSummary {
  total: number
  created: number
  updated: number
  skipped: number
  failed: number
}

// --- API functions ---

export function importsApi(api: ApiClient) {
  return {
    /** GET /import/spec — returns import file format specification as markdown. */
    getSpec: () =>
      api.fetch('/import/spec').then((r) => r.text()),

    /** POST /import — upload a YAML or JSON import file. */
    upload: async (fileOrContent: File | string): Promise<ImportUploadResponse> => {
      if (fileOrContent instanceof File) {
        // Multipart upload
        const formData = new FormData()
        formData.append('file', fileOrContent)
        const response = await api.fetch('/import', {
          method: 'POST',
          body: formData,
        })
        return response.json() as Promise<ImportUploadResponse>
      }
      // Raw content — detect format and set Content-Type
      const trimmed = fileOrContent.trim()
      const isJSON = trimmed.startsWith('{') || trimmed.startsWith('[')
      const response = await api.fetch('/import', {
        method: 'POST',
        headers: {
          'Content-Type': isJSON ? 'application/json' : 'application/x-yaml',
        },
        body: fileOrContent,
      })
      return response.json() as Promise<ImportUploadResponse>
    },

    /** GET /import/{id} — retrieve a pending import. */
    get: (importId: string) =>
      api.get<PendingImport>(`/import/${importId}`),

    /** PUT /import/{id} — update a pending import with new content. */
    update: async (importId: string, fileOrContent: File | string): Promise<ImportUploadResponse> => {
      if (fileOrContent instanceof File) {
        const formData = new FormData()
        formData.append('file', fileOrContent)
        const response = await api.fetch(`/import/${importId}`, {
          method: 'PUT',
          body: formData,
        })
        return response.json() as Promise<ImportUploadResponse>
      }
      const trimmed = fileOrContent.trim()
      const isJSON = trimmed.startsWith('{') || trimmed.startsWith('[')
      const response = await api.fetch(`/import/${importId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': isJSON ? 'application/json' : 'application/x-yaml',
        },
        body: fileOrContent,
      })
      return response.json() as Promise<ImportUploadResponse>
    },

    /** POST /import/{id}/validate — re-validate stored payload. */
    validate: (importId: string) =>
      api.post<ImportValidateResponse>(`/import/${importId}/validate`),

    /** POST /import/{id}/execute — execute the import using session auth. */
    execute: (importId: string) =>
      api.post<ExecutionReport>(`/import/${importId}/execute`),
  }
}
